import Box from '@mui/material/Box';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import LogoLoader from 'src/components/LogoLoader';
import {
  GetFrontendFeatureFlags,
  GetFrontendFeatureFlagsResponse,
} from 'src/configs/APISchemas/GetFrontendFeatureFlags.schema';
import { FeatureFlag } from 'src/constants/feature-flag';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { useMount } from 'src/helpers/hooks/useMount';

type FeatureFlagsLoadingStatus = 'pending' | 'loading' | 'resolved';
const FeatureFlagsLoadingStatus = {
  Loading: 'loading',
  Pending: 'pending',
  Resolved: 'resolved',
} as const;

type FeatureFlagsState = Record<FeatureFlag, boolean>;
interface FeatureFlagsContext {
  featureFlags: FeatureFlagsState;
}

const featureFlagsContext = createContext<FeatureFlagsContext | null>(null);

/**
 * Custom hook that provides access to the feature flags context.
 * Do not use this hook directly, use `useFeatureFlags` hook instead.
 * @returns The feature flags context.
 * @throws {Error} If used outside of a FeatureFlagsProvider.
 */
export const useInternalFeatureFlagsProvider = () => {
  const context = useContext(featureFlagsContext);

  if (!context) {
    throw new Error(
      'useFeatureFlagsProvider must be used within a FeatureFlagsProvider',
    );
  }

  return context;
};

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const [loadingStatus, setLoadingStatus] = useState<FeatureFlagsLoadingStatus>(
    FeatureFlagsLoadingStatus.Pending,
  );
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsState>(
    {} as FeatureFlagsState,
  );

  const fetchFeatureFlags = useCallback(async () => {
    setLoadingStatus(FeatureFlagsLoadingStatus.Loading);
    const requestQuery = GetFrontendFeatureFlags();

    try {
      const response = await GraphQLRequest<GetFrontendFeatureFlagsResponse>(
        requestQuery,
      );

      const flags = retrieveFeatureFlagsFromResponse(response);

      setFeatureFlags(flags);
    } finally {
      setLoadingStatus(FeatureFlagsLoadingStatus.Resolved);
    }
  }, []);

  useMount(() => {
    fetchFeatureFlags();
  });

  const contextValue = useMemo(() => {
    return {
      featureFlags,
    };
  }, [featureFlags]);

  const renderedChildren =
    loadingStatus === FeatureFlagsLoadingStatus.Resolved ? (
      children
    ) : (
      <FeatureFlagLoader />
    );

  return (
    <featureFlagsContext.Provider value={contextValue}>
      {renderedChildren}
    </featureFlagsContext.Provider>
  );
}

function retrieveFeatureFlagsFromResponse(
  response: GetFrontendFeatureFlagsResponse,
) {
  const flags = response.getFeatureFlags ?? {};

  const retrievedFeatureFlags = Object.entries(flags).reduce<FeatureFlagsState>(
    (accFlags, [key, value]) => {
      const isErrorsKeyOrValue = key === 'errors' || Array.isArray(value);

      if (isErrorsKeyOrValue) {
        return accFlags;
      }

      const flagValue = Boolean(value?.enabled);

      return {
        ...accFlags,
        [key]: flagValue,
      };
    },
    {} as FeatureFlagsState,
  );

  return retrievedFeatureFlags;
}

function FeatureFlagLoader() {
  return (
    <Box
      position="fixed"
      zIndex="2000"
      paddingBottom={{ xs: 30, lg: 30 }}
      height="100%"
      width="100%"
      sx={{ bgcolor: 'white' }}
    >
      <Box
        position="absolute"
        height="80vh"
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <LogoLoader />
      </Box>
    </Box>
  );
}
